import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Loader2, Plus, FileText, Calendar, Eye } from 'lucide-react'

interface ProposalSummary {
  id: string
  quotationNumber: string
  projectName: string
  customerName: string
  projectType: string
  status: string
  grandTotal: number
  currency: string
  createdAt: string
  validUntil: string
}

export default function ProposalsListPage() {
  const navigate = useNavigate()
  const [proposals, setProposals] = useState<ProposalSummary[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchProposals()
  }, [])

  const fetchProposals = async () => {
    try {
      const response = await fetch('/api/quotations/list')
      const data = await response.json()
      if (data.success) {
        setProposals(data.quotations)
      }
    } catch (error) {
      console.error('Error fetching proposals:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      draft: { label: 'Draft', variant: 'secondary' },
      sent: { label: 'Sent', variant: 'default' },
      accepted: { label: 'Accepted', variant: 'default' },
      rejected: { label: 'Rejected', variant: 'destructive' },
    }
    const statusInfo = statusMap[status] || { label: status, variant: 'outline' as const }
    return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#D32F2F] mx-auto mb-4" />
          <p className="text-lg text-slate-600">Loading proposals...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <header className="border-b bg-white">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="/" className="inline-block">
                <img
                  src="/sika-logo.svg"
                  alt="Sika"
                  width={100}
                  height={33}
                  className="opacity-80 hover:opacity-100 transition-opacity"
                />
              </a>
              <div className="border-l pl-4">
                <h1 className="text-2xl font-bold text-slate-900">Saved Proposals</h1>
                <p className="text-sm text-slate-600">Manage all your proposals</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" asChild>
                <a href="/">Back</a>
              </Button>
              <Button asChild>
                <a href="/proposal/new">
                  <Plus className="w-4 h-4 mr-2" />
                  New Proposal
                </a>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {proposals.length === 0 ? (
          <Card className="max-w-2xl mx-auto text-center py-12">
            <CardHeader>
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <CardTitle>No saved proposals</CardTitle>
              <CardDescription>
                Start by creating your first proposal using the AI assistant
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild>
                <a href="/proposal/new">
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Proposal
                </a>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {proposals.map((proposal) => (
              <Card
                key={proposal.id}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/proposals/${proposal.id}`)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-1">
                        {proposal.quotationNumber}
                      </CardTitle>
                      <CardDescription className="line-clamp-1">
                        {proposal.projectName}
                      </CardDescription>
                    </div>
                    {getStatusBadge(proposal.status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center text-sm text-slate-600">
                    <FileText className="w-4 h-4 mr-2" />
                    {proposal.projectType}
                  </div>
                  <div className="flex items-center text-sm text-slate-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    {formatDate(proposal.createdAt)}
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t">
                    <span className="text-sm text-slate-600">Total:</span>
                    <span className="text-lg font-bold text-[#D32F2F]">
                      {proposal.grandTotal.toFixed(2)} {proposal.currency}
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`/proposals/${proposal.id}`)
                    }}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Details
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
